import { useState, useMemo, useCallback } from 'react';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import Pagination from './pagination';
import { useServerPage } from '../use_server_page';
import { _js } from '@ifixit/utils/localize';
import { SectionHeading } from './section_heading';
import { ResourceCard } from '@ifixit/ui/components/deprecated-card';

// Constants
const MAX_ITEMS = 4;

// Type definitions
interface PageContext {
   type: string;
   curPage: number;
   totalPages: number;
   prevPage: boolean;
   nextPage: boolean;
}

interface BlurbItem {
   title: string;
   guideUrl: string;
   thumbnail: string;
   device?: string;
}

interface InfoItem {
   title: string;
   url: string;
   date_html?: string;
   text?: string;
}

interface CategoryData {
   blurbs?: BlurbItem[];
   info?: InfoItem[];
   pageContext?: PageContext;
}

interface CategoryType {
   id: string;
   label: string;
   data: CategoryData;
}

interface ContributionsData {
   privateGuideData?: CategoryData;
   teardownsAuthored?: CategoryData;
   teardownsContributed?: CategoryData;
   teardownsTranslated?: CategoryData;
   authored?: CategoryData;
   contributed?: CategoryData;
   translated?: CategoryData;
   wikisAuthored?: CategoryData;
   wikisContributed?: CategoryData;
   wikisTranslated?: CategoryData;
   favorites?: CategoryData;
   successes?: CategoryData;
   stories?: CategoryData;
   questions?: CategoryData;
   answers?: CategoryData;
   comments?: CategoryData;
}

// Custom hook for paginated blurb data. The first page arrived with the tab
// and is read from it; only the other pages are fetched.
function usePaginatedBlurb(
   userid: number,
   initialPageContext: PageContext,
   initialData: CategoryData
) {
   const [pageContext, setPageContext] = useState(initialPageContext);
   const isOnInitialPage = pageContext.curPage === initialPageContext.curPage;
   const { data, isLoading, error } = useServerPage<CategoryData>(
      `users/${userid}/blurb/${initialPageContext.type}/${pageContext.curPage}`,
      initialData,
      isOnInitialPage
   );

   return {
      data,
      isLoading,
      error,
      pageContext,
      setPageContext,
      showPagination: initialPageContext.totalPages > 1,
   };
}

export const Contributions = ({ data, userid }: { data: ContributionsData; userid: number }) => {
   // Use useMemo to avoid recreating categories array on every render
   const categories = useMemo<CategoryType[]>(
      () => [
         {
            id: 'private',
            label: _js("Unpublished Guides I've Worked On"),
            data: data.privateGuideData ?? {},
         },
         {
            id: 'teardownsAuthored',
            label: _js('My Teardowns'),
            data: data.teardownsAuthored ?? {},
         },
         {
            id: 'teardownsContributed',
            label: _js("Teardowns I've Contributed To"),
            data: data.teardownsContributed ?? {},
         },
         {
            id: 'teardownsTranslated',
            label: _js("Teardowns I've Helped Translate"),
            data: data.teardownsTranslated ?? {},
         },
         { id: 'guidesAuthored', label: _js('My Guides'), data: data.authored ?? {} },
         {
            id: 'guidesContributed',
            label: _js("Guides I've Contributed To"),
            data: data.contributed ?? {},
         },
         {
            id: 'guidesTranslated',
            label: _js("Guides I've Helped Translate"),
            data: data.translated ?? {},
         },
         {
            id: 'wikisAuthored',
            label: _js('My Wikis'),
            data: data.wikisAuthored ?? {},
         },
         {
            id: 'wikisContributed',
            label: _js("Wikis I've Contributed To"),
            data: data.wikisContributed ?? {},
         },
         {
            id: 'wikisTranslated',
            label: _js("Wikis I've Helped Translate"),
            data: data.wikisTranslated ?? {},
         },
         { id: 'favoritedGuides', label: _js('My Favorite Guides'), data: data.favorites ?? {} },
         { id: 'success', label: _js('Completed Guides'), data: data.successes ?? {} },
         { id: 'stories', label: _js('My Stories'), data: data.stories ?? {} },
         { id: 'questions', label: _js('Questions'), data: data.questions ?? {} },
         { id: 'answers', label: _js('Answers'), data: data.answers ?? {} },
         { id: 'comments', label: _js('Guide Comments'), data: data.comments ?? {} },
      ],
      [data]
   );

   // Simplified conditional using optional chaining
   const showBlurb = useCallback(
      (blurbData: CategoryData): boolean =>
         (blurbData?.blurbs?.length ?? 0) > 0 || (blurbData?.info?.length ?? 0) > 0,
      []
   );

   return (
      <div className="flex flex-col items-start max-w-[646px] gap-6">
         <SectionHeading>{_js('Contributions')}</SectionHeading>
         {categories.map((category: CategoryType) =>
            showBlurb(category.data) ? (
               <GuideBlurb key={category.id} category={category} userid={userid} />
            ) : null
         )}
      </div>
   );
};

const GuideBlurb = ({ category, userid }: { category: CategoryType; userid: number }) => {
   const data = category.data;
   const pageContext = data.pageContext;
   return (
      <div className={`${category.id} grid gap-4 w-full`} key={category.id}>
         <h3 className="heading-24" id={category.id}>
            {category.label}
         </h3>
         {data.blurbs && pageContext ? (
            <BlurbGrid initialPageContext={pageContext} initialData={data} userid={userid} />
         ) : null}
         {data.info && pageContext ? (
            <BlurbList initialPageContext={pageContext} initialData={data} userid={userid} />
         ) : null}
      </div>
   );
};

const BlurbGrid = ({
   initialPageContext,
   initialData,
   userid,
}: {
   initialPageContext: PageContext;
   initialData: CategoryData;
   userid: number;
}) => {
   const { data, isLoading, error, pageContext, setPageContext, showPagination } =
      usePaginatedBlurb(userid, initialPageContext, initialData);

   if (error) {
      return <BlurbErrorMessage />;
   }

   return (
      <>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isLoading ? (
               <BlurbGridSkeleton length={MAX_ITEMS} />
            ) : data ? (
               <BlurbGridContent data={data} />
            ) : null}
         </div>
         {showPagination && (
            <Pagination
               pageContext={pageContext}
               setPageContext={setPageContext}
               className="rounded-xl"
            />
         )}
      </>
   );
};

const BlurbGridSkeleton = ({ length }: { length: number }) => (
   <>
      {Array.from({ length }, (_, i) => (
         <Skeleton key={i} className="h-[90px] rounded-2xl" />
      ))}
   </>
);

const BlurbGridContent = ({ data }: { data: CategoryData }) => (
   <>
      {data.blurbs?.map(blurb => (
         <BlurbGridItem key={blurb.title} blurb={blurb} />
      ))}
   </>
);

const BlurbGridItem = ({ blurb }: { blurb: BlurbItem }) => (
   <ResourceCard
      url={blurb.guideUrl}
      thumbnail={blurb.thumbnail}
      title={blurb.title}
      device={blurb.device || undefined}
   />
);

const BlurbList = ({
   initialPageContext,
   initialData,
   userid,
}: {
   initialPageContext: PageContext;
   initialData: CategoryData;
   userid: number;
}) => {
   const { data, isLoading, error, pageContext, setPageContext, showPagination } =
      usePaginatedBlurb(userid, initialPageContext, initialData);

   if (error) {
      return <BlurbErrorMessage />;
   }

   return (
      <>
         {isLoading ? (
            <BlurbListSkeleton length={MAX_ITEMS} showPagination={showPagination} />
         ) : data ? (
            <BlurbListContent data={data} showPagination={showPagination} />
         ) : null}
         {showPagination && (
            <Pagination
               pageContext={pageContext}
               setPageContext={setPageContext}
               className="rounded-xl"
            />
         )}
      </>
   );
};

const BlurbListSkeleton = ({
   length,
   showPagination,
}: {
   length: number;
   showPagination: boolean;
}) => (
   <div
      className="flex flex-col gap-2 pt-1.5"
      style={{ minHeight: showPagination ? '288px' : '280px' }}
   >
      {Array.from({ length }, (_, i) => (
         <Skeleton key={i} className="h-[19px]" />
      ))}
   </div>
);

const BlurbListContent = ({
   data,
   showPagination,
}: {
   data: CategoryData;
   showPagination: boolean;
}) => {
   if (!data?.info) {
      return null;
   }

   return (
      <div
         className="flex flex-col gap-2 pt-1.5"
         style={{ minHeight: showPagination ? '280px' : 'unset' }}
      >
         {data.info.map((info, index) => (
            <BlurbListItem key={info.title + index} info={info} />
         ))}
      </div>
   );
};

const BlurbListItem = ({ info }: { info: InfoItem }) => (
   <div className="flex flex-col w-full">
      <div className="flex items-center justify-between w-full gap-1">
         <a
            className="flex-1 m-0 mr-2 font-semibold overflow-hidden text-ellipsis line-clamp-1"
            href={info.url}
         >
            {info.title}
         </a>
         {info.date_html ? (
            <div
               className="ml-auto text-gray-400 whitespace-nowrap font-normal text-sm"
               dangerouslySetInnerHTML={{ __html: info.date_html }}
            />
         ) : null}
      </div>
      {info.text ? (
         <div
            className="text-sm text-gray-500 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: info.text }}
         />
      ) : null}
   </div>
);

const BlurbErrorMessage = () => <div>{_js('Something went wrong.')}</div>;
